import { getDownloadURL, getStorage, list, ref, type StorageReference } from "firebase/storage";
import { archivedMealImagesStoragePath, mealsPath } from "../../functions/src/admin/paths";
import type { Language } from "./langConfig";
import { currentCatering, functions } from "./firebase";

/** Upload a file or blob to a signed URL (PUT). Optionally set download filename via Content-Disposition (e.g. for PDFs). */
async function uploadToSignedUrl(uploadUrl: string, body: Blob | File, contentType: string, downloadFilename?: string): Promise<void> {
    const headers: Record<string, string> = { 'Content-Type': contentType };
    if (downloadFilename) {
        headers['Content-Disposition'] = `attachment; filename="${downloadFilename}"`;
    }
    const res = await fetch(uploadUrl, {
        method: 'PUT',
        body,
        headers,
    });
    if (!res.ok) {
        throw new Error(`Upload failed: ${res.status} ${res.statusText}`);
    }
}

/**
 * Image Utilities for Firebase Storage Upload
 * 
 * This module provides shared functionality for image uploading across the application.
 * It includes image resizing, Firebase Storage upload, and file system access for
 * finding photos in the public/images/temp directory.
 */

// Image resizing utility function
export const resizeImage = (file: File, maxWidth: number = 800, maxHeight: number = 600, quality: number = 0.8): Promise<Blob> => {
    return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        const img = new Image();

        img.onload = () => {
            // Calculate new dimensions while maintaining aspect ratio
            let { width, height } = img;
            
            if (width > height) {
                if (width > maxWidth) {
                    height = (height * maxWidth) / width;
                    width = maxWidth;
                }
            } else {
                if (height > maxHeight) {
                    width = (width * maxHeight) / height;
                    height = maxHeight;
                }
            }

            // Set canvas dimensions
            canvas.width = width;
            canvas.height = height;

            // Draw and resize image
            ctx.drawImage(img, 0, 0, width, height);

            // Convert to blob
            canvas.toBlob((blob) => {
                resolve(blob!);
            }, 'image/jpeg', quality);
        };

        img.src = URL.createObjectURL(file);
    });
};

// Upload image to Firebase Storage via signed URL (admin-only, server-validated).
export const uploadImageToStorage = async (file: File, storagePath: string): Promise<string> => {
    try {
        const resizedImageBlob = await resizeImage(file, 800, 600, 0.8);
        const contentType = 'image/jpeg';
        const { data } = await functions.httpsCallable('getStorageUploadSignedUrl')({
            path: storagePath,
            contentType,
        });
        const { uploadUrl, gsUrl } = data as { uploadUrl: string; gsUrl: string };
        await uploadToSignedUrl(uploadUrl, resizedImageBlob, contentType);
        return gsUrl;
    } catch (error) {
        console.error('Error uploading image:', error);
        throw error;
    }
};

export type MealStorageImageItem = {
    fullPath: string;
    gsUrl: string;
    source: "meals" | "archive";
    downloadUrl: string;
};

async function listImageRefsInFolder(folderPath: string): Promise<StorageReference[]> {
    const storage = getStorage();
    const folderRef = ref(storage, folderPath);
    const refs: StorageReference[] = [];
    let pageToken: string | undefined;
    do {
        const page = await list(folderRef, { maxResults: 100, pageToken });
        for (const item of page.items) {
            if (/\.(jpe?g|png|gif|webp)$/i.test(item.name)) {
                refs.push(item);
            }
        }
        pageToken = page.nextPageToken;
    } while (pageToken);
    return refs;
}

/** Lists image files under active and archived meal storage folders (paginated). */
export async function listMealImagesForCatering(cateringId: string): Promise<MealStorageImageItem[]> {
    const storage = getStorage();
    const bucket = storage.app.options.storageBucket as string;
    const results: MealStorageImageItem[] = [];

    const loadPrefix = async (prefix: string, source: "meals" | "archive") => {
        const refs = await listImageRefsInFolder(prefix);
        for (const itemRef of refs) {
            const fullPath = itemRef.fullPath;
            const gsUrl = `gs://${bucket}/${fullPath}`;
            const downloadUrl = await getDownloadURL(itemRef);
            results.push({ fullPath, gsUrl, source, downloadUrl });
        }
    };

    await loadPrefix(mealsPath(cateringId), "meals");
    await loadPrefix(archivedMealImagesStoragePath(cateringId), "archive");

    return results.sort((a, b) => a.fullPath.localeCompare(b.fullPath));
}

/** Get download URL for a file in Storage if it exists; returns null if not found. */
export const getExistingPdfDownloadUrl = async (storagePath: string): Promise<string | null> => {
    try {
        return await getDownloadURL(ref(getStorage(), storagePath));
    } catch {
        return null;
    }
};

/** School admins: server resolves Storage path from permitted school + month (do not trust client path). */
export type AllergenPdfSchoolAdminParams = { schoolKey: string; yearMonth: string; lang: Language };

/** Delete a file from Firebase Storage (global admin: path; school admin: allergenSchoolAdmin). */
export const deleteFileFromStorage = async (
    storagePathOrAllergen: string | AllergenPdfSchoolAdminParams
): Promise<void> => {
    const payload =
        typeof storagePathOrAllergen === 'string'
            ? { path: storagePathOrAllergen }
            : { allergenSchoolAdmin: storagePathOrAllergen };
    await functions.httpsCallable('storageDeleteFile')(payload);
};

/** Upload any file via signed URL (global admin: storage path). Preserves file type and download name. */
export const uploadFileToStorage = async (file: File, storagePath: string): Promise<string> => {
    try {
        const contentType = file.type || 'application/octet-stream';
        const { data } = await functions.httpsCallable('getStorageUploadSignedUrl')({ path: storagePath, contentType });
        const { uploadUrl, gsUrl } = data as { uploadUrl: string; gsUrl: string };
        await uploadToSignedUrl(uploadUrl, file, contentType, file.name);
        return gsUrl;
    } catch (error) {
        console.error('Error uploading file:', error);
        throw error;
    }
};

/** Upload a PDF via signed URL (global admin: storage path; school admin: allergenSchoolAdmin). */
export const uploadPdfToStorage = async (
    file: File,
    storagePathOrAllergen: string | AllergenPdfSchoolAdminParams
): Promise<string> => {
    try {
        const contentType = 'application/pdf';
        const payload =
            typeof storagePathOrAllergen === 'string'
                ? { path: storagePathOrAllergen, contentType }
                : { contentType, allergenSchoolAdmin: storagePathOrAllergen };
        const { data } = await functions.httpsCallable('getStorageUploadSignedUrl')(payload);
        const { uploadUrl, gsUrl } = data as { uploadUrl: string; gsUrl: string };
        const downloadFilename =
            typeof storagePathOrAllergen === 'string'
                ? storagePathOrAllergen.split('/').pop()
                : `${storagePathOrAllergen.yearMonth}-${storagePathOrAllergen.lang}-allergens.pdf`;
        await uploadToSignedUrl(uploadUrl, file, contentType, downloadFilename);
        return gsUrl;
    } catch (error) {
        console.error('Error uploading PDF:', error);
        throw error;
    }
};

// Upload meal photo with specific naming convention
export const uploadMealPhoto = async (file: File, mealKey: string): Promise<string> => {
    const fileName = `${mealKey}_${Date.now()}.jpg`;
    const storagePath = `meals/${currentCatering}/${fileName}`;
    return await uploadImageToStorage(file, storagePath);
};

// Check if file exists in public/images/temp folder and convert to File object
export const findPhotoFile = async (searchPattern: string): Promise<File | null> => {
    try {
        console.log(`Looking for photo file matching pattern: ${searchPattern}`);
        
        // Since we know the files exist in public/images/temp, we can try to fetch them directly
        // We'll try common extensions and see if the file exists
        const extensions = ['.jpg', '.jpeg', '.png', '.gif'];
        
        for (const ext of extensions) {
            const filename = `${searchPattern}${ext}`;
            const imageUrl = `/images/temp/${filename}`;
            
            try {
                console.log(`Trying to fetch: ${imageUrl}`);
                const response = await fetch(imageUrl);
                
                if (response.ok) {
                    // File exists, convert to File object
                    const blob = await response.blob();
                    const file = new File([blob], filename, { type: blob.type });
                    
                    console.log(`✅ Found photo file: ${filename}`);
                    return file;
                }
            } catch (fetchError) {
                // Continue to next extension
                continue;
            }
        }
        
        // If exact match not found, try to find files that start with the pattern
        // This handles cases where the file might have additional characters after the pattern
        try {
            console.log(`Exact match not found, trying to find files starting with: ${searchPattern}`);
            
            // For now, we'll just log that we need to implement directory listing
            // In a real implementation, you might want to:
            // 1. Use a server endpoint to list files in the directory
            // 2. Or maintain a manifest file of available images
            // 3. Or use a different approach to find partial matches
            
            console.log(`Note: To find partial matches (like ${searchPattern}.*), you would need to implement directory listing`);
            
        } catch (error) {
            console.error('Error in partial match search:', error);
        }
        
        console.log(`❌ No photo file found for pattern: ${searchPattern}`);
        return null;
        
    } catch (error) {
        console.error('Error finding photo file:', error);
        return null;
    }
}; 